import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import 'mantine-datatable/styles.css'

import { useEffect, useRef, useState } from 'react'
import { MantineProvider } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import { RouterProvider } from 'react-router'
import { theme } from './theme/theme'
import { router } from './router'
import { LoginView } from './views/auth/LoginView'
import { ChangePasswordView } from './views/auth/ChangePasswordView'
import { PasskeyConfirmView } from './views/auth/PasskeyConfirmView'
import { useAuthStore } from './stores/authStore'
import { Center, Loader, Stack, Text } from '@mantine/core'
import { authClient } from './api/authClient'

const CONFIRM_PATH = '/passkeys/confirm'

/** Pure read, safe to call more than once (React 18 StrictMode double-invokes lazy state
 * initializers in dev) — must not be combined with the `history.replaceState` mutation
 * below, or a second invocation would read back the already-stripped URL and clobber the
 * token with `null`. */
function extractConfirmToken(): string | null {
  if (window.location.pathname !== CONFIRM_PATH) return null
  return new URLSearchParams(window.location.search).get('token')
}

function AppContent() {
  const { isAuthenticated, isInitializing, mustChangePassword, initialize } = useAuthStore()

  const [confirmToken, setConfirmToken] = useState<string | null>(() => extractConfirmToken())
  const [resumedToken, setResumedToken] = useState<string | null>(null)
  const hasResumedRef = useRef(false)

  useEffect(() => {
    initialize()
  }, [initialize])

  // Drops the token from the address bar (security review M5 pattern) so it doesn't survive
  // in browser history or a Referer header. Deliberately never reads the URL back out —
  // just strips it — so it's harmless if StrictMode invokes it twice.
  useEffect(() => {
    if (window.location.pathname === CONFIRM_PATH) {
      window.history.replaceState({}, '', CONFIRM_PATH)
    }
  }, [])

  // No session yet (the common cross-device case, build plan §4.1): stash the token and
  // fall through to the normal login flow below. Picked back up once authenticated.
  useEffect(() => {
    if (confirmToken && !isAuthenticated && !isInitializing) {
      authClient.passkeys.stashPendingEnrollment(confirmToken)
      setConfirmToken(null)
    }
  }, [confirmToken, isAuthenticated, isInitializing])

  useEffect(() => {
    // hasResumedRef guards against StrictMode's double-invoke: takePendingEnrollment()
    // clears sessionStorage as it reads, so a second call would silently see nothing and
    // overwrite the correct result with null.
    if (isAuthenticated && !confirmToken && !hasResumedRef.current) {
      hasResumedRef.current = true
      const stashed = authClient.passkeys.takePendingEnrollment()
      if (stashed) {
        setResumedToken(stashed)
      }
    }
  }, [isAuthenticated, confirmToken])

  if (isInitializing) {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed" size="sm">Loading...</Text>
        </Stack>
      </Center>
    )
  }

  if (!isAuthenticated) {
    return <LoginView />
  }

  if (mustChangePassword) {
    return <ChangePasswordView />
  }

  const activeConfirmToken = confirmToken ?? resumedToken
  if (activeConfirmToken) {
    return (
      <PasskeyConfirmView
        token={activeConfirmToken}
        onDone={() => {
          window.location.href = '/settings'
        }}
      />
    )
  }

  return <RouterProvider router={router} />
}

export function App() {
  return (
    <MantineProvider theme={theme}>
      <ModalsProvider>
        <Notifications position="top-right" />
        <AppContent />
      </ModalsProvider>
    </MantineProvider>
  )
}
